import type { EventType, ProjectState } from "@livcg/schema";
import {
  Activity,
  ArrowLeftRight,
  EyeOff,
  Flag,
  Grid3x3,
  Keyboard,
  ListOrdered,
  Settings,
  ShieldQuestion,
  SquareStack,
  Target,
} from "lucide-react";
import { useRef, useState } from "react";
import { sendRaw } from "../shared/sendRaw.js";
import { useHotkeys } from "../shared/useHotkeys.js";
import { Button, StatusDot, type Tab, Tabs } from "../ui/index.js";
import { allOffSurfaces } from "./allOff.js";
import { Drawer } from "./control-room/Drawer.js";
import { GridPanel } from "./control-room/GridPanel.js";
import { OverlayBoardView } from "./control-room/OverlayBoard.js";
import { PenaltyPanel } from "./control-room/PenaltyPanel.js";
import { PlayerCompareDrawer } from "./control-room/player-compare/PlayerCompareDrawer.js";
import { QuickEvents, type QuickEventsHandle } from "./control-room/QuickEvents.js";
import { SetupDrawer } from "./control-room/SetupDrawer.js";
import { ShortcutsHelp } from "./control-room/ShortcutsHelp.js";
import { useOverlayExtras } from "./control-room/useOverlayExtras.js";
import { useOverlaySwitch } from "./control-room/useOverlaySwitch.js";
import { MatchDayHeader } from "./Header.js";
import { overlayModeLabel } from "./overlayModes.js";
import { TabLog } from "./TabLog.js";
import { TabStats } from "./TabStats.js";
import { TabVar } from "./TabVar.js";

type TabId =
  | "events"
  | "modes"
  | "var"
  | "lineup"
  | "stats"
  | "players"
  | "penalties"
  | "log"
  | "setup";

const TABS: readonly { id: TabId; label: string; icon: typeof Flag }[] = [
  { id: "events", label: "Events", icon: Flag },
  { id: "modes", label: "Overlay", icon: SquareStack },
  { id: "var", label: "VAR", icon: ShieldQuestion },
  { id: "lineup", label: "Lineup", icon: Grid3x3 },
  { id: "stats", label: "Statistika", icon: Activity },
  { id: "players", label: "O'yinchilar", icon: ArrowLeftRight },
  { id: "penalties", label: "Penalti", icon: Target },
  { id: "log", label: "Jurnal", icon: ListOrdered },
  { id: "setup", label: "Sozlamalar", icon: Settings },
];

/** The tab-based control surface. It owns one overlay switch and one quick
 *  event panel ref, so the Control Room hotkeys work here from any tab. */
export function ClassicView({ slug, state }: { slug: string; state: ProjectState }) {
  const [tab, setTab] = useState<TabId>("events");
  const [helpOpen, setHelpOpen] = useState(false);
  const extrasCtx = useOverlayExtras(state);
  const sw = useOverlaySwitch(slug, state, extrasCtx.extras);
  const quickRef = useRef<QuickEventsHandle>(null);
  const allOff = () => allOffSurfaces(slug, state);

  // G/Y/R/S act on the player picked in Events — elsewhere they open it.
  const fireEvent = (type: EventType) => {
    if (tab === "events") quickRef.current?.fire(type);
    else setTab("events");
  };

  useHotkeys({
    space: () => void sendRaw("clock.toggle", { slug }),
    g: () => fireEvent("goal"),
    y: () => fireEvent("yellow_card"),
    r: () => fireEvent("red_card"),
    s: () => fireEvent("substitution"),
    esc: () => {
      if (!helpOpen) sw.hideCurrent();
    },
    "shift+esc": () => allOff(),
    "shift+/": () => setHelpOpen(true),
  });

  const tabs: Tab<TabId>[] = TABS.map((t) => ({
    id: t.id,
    label: (
      <span className="inline-flex items-center gap-1.5 whitespace-nowrap">
        <t.icon className="h-3.5 w-3.5" strokeWidth={2.5} />
        {t.label}
      </span>
    ),
  }));

  return (
    <>
      <MatchDayHeader slug={slug} state={state} />

      <nav className="border-b border-divider bg-surface/30 px-8 py-3">
        <div className="mx-auto flex w-full max-w-6xl items-center gap-2">
          <div className="min-w-0 overflow-x-auto">
            <Tabs<TabId> tabs={tabs} value={tab} onChange={setTab} />
          </div>
          <div className="ml-auto flex shrink-0 items-center gap-2">
            {sw.current ? (
              <>
                <span className="inline-flex items-center gap-1.5 rounded-md border border-live/40 bg-live-soft px-2 py-0.5 text-[11px] font-semibold text-live">
                  <StatusDot tone="live" pulse />
                  {overlayModeLabel(sw.current)}
                </span>
                <Button
                  variant="secondary"
                  size="xs"
                  onClick={sw.hideCurrent}
                  leadingIcon={<EyeOff className="h-3 w-3" />}
                >
                  Yashirish
                </Button>
              </>
            ) : (
              <span className="text-[11px] text-ink-faint">Efirda overlay yo'q</span>
            )}
            <Button
              variant="ghost"
              size="xs"
              iconOnly
              aria-label="Klaviatura yorliqlari"
              title="Klaviatura yorliqlari (?)"
              onClick={() => setHelpOpen(true)}
            >
              <Keyboard className="h-3.5 w-3.5" />
            </Button>
          </div>
        </div>
      </nav>

      <div className="flex-1 overflow-y-auto px-8 py-6">
        <div className="mx-auto w-full max-w-5xl">
          {tab === "events" && (
            <QuickEvents slug={slug} state={state} controlRef={quickRef} wide />
          )}
          {tab === "modes" && (
            <OverlayBoardView
              slug={slug}
              state={state}
              onAllOff={allOff}
              showClassicOnly
              extrasCtx={extrasCtx}
              sw={sw}
            />
          )}
          {tab === "var" && <TabVar slug={slug} state={state} />}
          {tab === "lineup" && (
            <GridPanel slug={slug} state={state} />
          )}
          {tab === "stats" && <TabStats slug={slug} state={state} />}
          {tab === "players" && <PlayerCompareDrawer slug={slug} state={state} />}
          {tab === "penalties" && <PenaltyPanel slug={slug} state={state} />}
          {tab === "log" && <TabLog slug={slug} state={state} />}
          {tab === "setup" && (
            <SetupDrawer slug={slug} state={state} inline onClose={() => setTab("events")} />
          )}
        </div>
      </div>

      <Drawer
        open={helpOpen}
        onClose={() => setHelpOpen(false)}
        title="Klaviatura yorliqlari"
        width="max-w-md"
      >
        <ShortcutsHelp />
      </Drawer>
    </>
  );
}
